/** Display formatting helpers shared across views. */

import { toNumber } from './calc';

/** Format a value as whole-dollar currency, or "--" when blank. */
export const formatCurrency = (value) => {
  const n = toNumber(value);
  if (n === null) return '--';
  return `$${Math.round(n).toLocaleString()}`;
};

/** Format a percentage value (a number like 13.9) as "13.9%", or "--". */
export const formatPercent = (value, digits = 1) => {
  const n = toNumber(value);
  if (n === null) return '--';
  return `${n.toFixed(digits)}%`;
};

/**
 * Format a signed percentage for gain/loss display.
 * Negative values render parenthesized, e.g. -24 -> "(24.0%)".
 */
export const formatGainLoss = (value, digits = 1) => {
  const n = toNumber(value);
  if (n === null) return '--';
  if (n < 0) return `(${Math.abs(n).toFixed(digits)}%)`;
  return `${n.toFixed(digits)}%`;
};

/**
 * Normalize a date string (e.g. "12/31/2019" or "2019-12-31") to ISO
 * "YYYY-MM-DD" for stable sorting/storage. Returns the input if unparseable.
 */
export const toIsoDate = (value) => {
  if (!value) return '';
  const str = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return str;
  return d.toISOString().slice(0, 10);
};

/** Format an ISO date as a short "Mon YYYY" label for charts. */
export const formatMonthLabel = (isoDate) => {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return String(isoDate);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

/** Format an ISO date as a full readable date, e.g. "December 31, 2019". */
export const formatFullDate = (isoDate) => {
  if (!isoDate) return '--';
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return String(isoDate);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

/** Latest Firestore updatedAt across all holdings. */
export const getLatestHoldingsUpdatedAt = (holdings) =>
  holdings.reduce((latest, holding) => {
    if (!holding.updatedAt) return latest;
    if (!latest) return holding.updatedAt;
    return holding.updatedAt.toDate() > latest.toDate() ? holding.updatedAt : latest;
  }, null);

/** Format the most recent holdings save as a readable date, or "N/A". */
export const formatHoldingsAsOf = (holdings) => {
  const lastUpdated = getLatestHoldingsUpdatedAt(holdings);
  if (!lastUpdated) return 'N/A';
  try {
    return formatFullDate(lastUpdated.toDate().toISOString());
  } catch {
    return 'N/A';
  }
};
